import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Form, Input, Segmented, Button, Alert, Space, Typography } from "antd";
import {
  DeleteOutlined,
  UserOutlined,
  CheckOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import Icon from "@ant-design/icons";
import { ReactComponent as CernLogoSvg } from "../../assets/icons/CernLogoOutlined.svg";

import AdminGroup from "../groups/AdminGroup";
import { useForm, Controller } from "react-hook-form";
import PropTypes from "prop-types";
import { run_env } from "../../config";
import styles from "./NewApplicationForm.module.css";
import CategoryOption from "../CategoryOption";

const { Text, Paragraph } = Typography;

const CernLogoIcon = (props) => <Icon component={CernLogoSvg} {...props} />;

const required = (value) => (value ? undefined : "Required");
const minLength = (min) => (value) =>
  value && value.length < min ? `Must be ${min} characters or more` : undefined;
const minLength3 = minLength(3);
const maxLength = (max) => (value) =>
  value && value.length > max ? `Must be ${max} characters or less` : undefined;
const maxLength127 = maxLength(127);
const homepage = (value) =>
  value &&
  !/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/.test(
    value,
  )
    ? "Invalid Home Page"
    : undefined;
const nospace = (value) =>
  value && !/^\S+$/.test(value) ? "Cannot contain a space" : undefined;
const identifier = (value) =>
  value && !/^[a-z][a-z0-9\-_]+$/.test(value)
    ? "Must start with a lowercase letter, can contain only lowercase letters, numbers, dashes and underscores"
    : undefined;

const NewApplicationForm = ({
  submitFunction,
  initialValues,
  isEdit,
  setEditMode,
  formValidation = null,
  formValues = null,
  isNewApplicationForm = false,
  onValidationChange = null,
}) => {
  const existingAppDefaultValues = () => ({
    applicationIdentifier: initialValues.applicationIdentifier,
    displayName: initialValues.displayName,
    description: initialValues.description,
    homePage: initialValues.homePage,
    administratorsId: initialValues.administratorsId,
    resourceCategory: initialValues.resourceCategory,
  });

  const newAppDefaultValues = () => ({
    applicationIdentifier: "",
    displayName: "",
    description: "",
    homePage: "",
    administratorsId: null,
    resourceCategory: "Official",
  });

  const defaultValues = () =>
    initialValues ? existingAppDefaultValues() : newAppDefaultValues();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    trigger,
    getValues,
  } = useForm({
    mode: "onChange",
    defaultValues: defaultValues(),
  });

  useEffect(() => {
    if (trigger && formValidation) {
      formValidation(() => trigger);
    }
  }, [trigger, formValidation]);

  useEffect(() => {
    if (getValues && formValues) {
      formValues(() => getValues);
    }
  }, [getValues, formValues]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  const onSubmit = (data) => {
    submitFunction(data);
    return;
  };

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
      <div style={{ marginBottom: 16 }} />

      {!isEdit && (
        <Form.Item
          style={{ marginBottom: 8 }}
          label={
            <span
              style={{
                display: "block",
                fontWeight: "bold",
              }}
            >
              Application Identifier
              <span style={{ marginLeft: 4 }} />
              <Text type="danger">*</Text>
            </span>
          }
        >
          <Controller
            name="applicationIdentifier"
            control={control}
            rules={{
              validate: {
                minLength3,
                nospace,
                identifier,
                maxLength127,
                required,
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                id="applicationIdentifierField"
                placeholder="Application's identifier in minimum 3 characters (e.g. 'myapp')"
              />
            )}
          />

          {errors.applicationIdentifier && (
            <Paragraph style={{ color: "red", marginTop: 4 }}>
              {errors.applicationIdentifier.message}
            </Paragraph>
          )}
        </Form.Item>
      )}

      <Form.Item
        style={{ marginBottom: 8 }}
        label={
          <span
            style={{
              display: "block",
              fontWeight: "bold",
            }}
          >
            Name
            <span style={{ marginLeft: 4 }} />
            <Text type="danger">*</Text>
          </span>
        }
      >
        <Controller
          name="displayName"
          control={control}
          rules={{ validate: { required } }}
          render={({ field }) => (
            <Input
              {...field}
              id="displayNameField"
              placeholder="Name for your application, e.g. 'My App'"
            />
          )}
        />

        {errors.displayName && (
          <Paragraph style={{ color: "red", marginTop: 4 }}>
            {errors.displayName.message}
          </Paragraph>
        )}
      </Form.Item>

      <Form.Item
        style={{ marginBottom: 8 }}
        label={
          <span
            style={{
              display: "block",
              fontWeight: "bold",
            }}
          >
            Home Page
          </span>
        }
      >
        <Controller
          name="homePage"
          control={control}
          rules={{ validate: { homepage } }}
          render={({ field }) => (
            <Input
              {...field}
              id="homePageField"
              placeholder="Home Page e.g. https://myapp.cern.ch"
            />
          )}
        />

        {errors.homePage && (
          <Paragraph style={{ color: "red", marginTop: 4 }}>
            {errors.homePage.message}
          </Paragraph>
        )}
      </Form.Item>

      <Form.Item
        style={{ marginBottom: 8 }}
        label={
          <span
            style={{
              display: "block",
              fontWeight: "bold",
            }}
          >
            Description
          </span>
        }
      >
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <Input.TextArea
              {...field}
              id="descriptionField"
              placeholder="Description of the purpose of your application"
              autoSize={{ minRows: 2, maxRows: 6 }}
            />
          )}
        />

        {errors.description && (
          <Paragraph style={{ color: "red", marginTop: 4 }}>
            {errors.description.message}
          </Paragraph>
        )}
      </Form.Item>

      <Form.Item
        label={
          <span
            style={{
              display: "block",
              fontWeight: "bold",
            }}
          >
            Administrators
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <Controller
          name="administratorsId"
          control={control}
          render={({ field }) => <AdminGroup input={field} />}
        />
      </Form.Item>

      <Form.Item
        label={
          <span style={{ display: "block", fontWeight: "bold" }}>
            Category
            <span style={{ marginLeft: 4 }} />
            <Text type="danger">*</Text>
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <Controller
          name="resourceCategory"
          control={control}
          rules={{ required: "Please select a category" }}
          render={({ field }) => (
            <Segmented
              {...field}
              id="resourceCategory"
              block
              value={field.value}
              onChange={field.onChange}
              options={[
                {
                  value: "Official",
                  label: (
                    <CategoryOption
                      icon={<CernLogoIcon style={{ fontSize: 40 }} />}
                      title="Official"
                      subtitle={`Recommended for projects and teams (Departments, Groups, Sections,
Projects, HEP, Experiments, Conferences, Events, Topics, Clubs, etc.)`}
                    />
                  ),
                },
                {
                  value: "Personal",
                  label: (
                    <CategoryOption
                      icon={<UserOutlined style={{ fontSize: 30 }} />}
                      title="Personal"
                      subtitle={`Recommended for personal sites (description of work, curriculum vitae, coordinates, academic qualifications, hobbies, etc.)`}
                    />
                  ),
                },
                {
                  value: "Test",
                  label: (
                    <CategoryOption
                      icon={<ExperimentOutlined style={{ fontSize: 30 }} />}
                      title="Test"
                      subtitle={`Recommended for testing (pre-production, experimental, etc.)`}
                    />
                  ),
                },
              ]}
            />
          )}
        />

        {errors.resourceCategory && (
          <Paragraph style={{ color: "red", marginTop: 4 }}>
            {errors.resourceCategory.message} || Please select a category
          </Paragraph>
        )}
      </Form.Item>

      <div style={{ marginTop: 12 }}>
        {(getValues("resourceCategory") === "Test" ||
          getValues("resourceCategory") === "Personal") &&
          run_env === "PROD" && (
            <Alert
              type="warning"
              showIcon
              message="Consider using our QA environment"
              description={
                <>
                  If you&apos;re creating a temporary or test site, please
                  consider using the
                  <a href="https://application-portal-qa.web.cern.ch">
                    {" "}
                    QA Applications Portal
                  </a>
                  .
                </>
              }
            />
          )}

        {getValues("resourceCategory") === "Official" && run_env === "QA" && (
          <Alert
            type="error"
            showIcon
            message="Careful! You're on QA"
            description={
              <>
                For official resources, make sure you use the production
                <a href="https://applications-portal.web.cern.ch/">
                  {" "}
                  Applications Portal
                </a>
              </>
            }
            style={{ marginTop: 8 }}
          />
        )}
      </div>

      {!isNewApplicationForm && (
        <div className={styles.buttons}>
          <Space
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button onClick={() => reset(defaultValues())} id="resetButton">
              Reset
            </Button>

            <Space>
              <Button
                danger
                color="red"
                variant="filled"
                onClick={() => setEditMode(false)}
                icon={<DeleteOutlined />}
                id="cancelApplicationEditingButton"
              >
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                id="submitButton"
                disabled={Object.keys(errors).length > 0}
                icon={<CheckOutlined />}
              >
                Submit
              </Button>
            </Space>
          </Space>
        </div>
      )}
    </Form>
  );
};

NewApplicationForm.propTypes = {
  submitFunction: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool.isRequired,
  setEditMode: PropTypes.func,
  formValidation: PropTypes.func,
  formValues: PropTypes.func,
  isNewApplicationForm: PropTypes.bool,
  onValidationChange: PropTypes.func,
};

export default connect(() => ({}))(NewApplicationForm);
